import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Heart, Sparkles } from 'lucide-react';
import { Header } from '@/components/layout/Header';
import { AuthenticatedHeader } from '@/components/layout/AuthenticatedHeader';
import { Footer } from '@/components/layout/Footer';
import { useMarketPartners } from '@/hooks/useMarketPartners';
import { useAuth } from '@/hooks/useAuth';
import { CategoryCard } from '@/components/gift-store/CategoryCard';
import { PartnerCard, PartnerEmptyState } from '@/components/gift-store/PartnerCard';
import { GIFT_CATEGORIES, getLocalizedCategory, getLocalizedCategories } from '@/components/gift-store/giftStoreCategories';
import { trackPartnerCardViewed } from '@/components/gift-store/giftStoreAnalytics';
import { Skeleton } from '@/components/ui/skeleton';
import { useLanguage } from '@/i18n/LanguageContext';
import { lc } from '@/i18n/copy';

const copy = {
  en: {
    badge: 'Partner Marketplace',
    heroTitle: 'Say It NOW Gift Store',
    heroDescription: 'Curated brands that make your words land even harder. Every partner is handpicked because we believe in what they do.',
    exclusiveNote: 'Exclusive discounts for Connection & Legacy members',
    browseTitle: 'Browse by Category',
    browseSubtitle: 'Select a category to see curated partners',
    clearFilter: 'Clear filter — show all partners',
    allPartners: 'All Partners',
    allPartnersDesc: 'Browse every curated brand in the Say It NOW marketplace.',
    showingResults: '✓ Showing results',
    affiliateNote: 'Some links are affiliate partnerships. If you choose to purchase, Say It NOW may earn a small commission at no extra cost to you.',
  },
  es: {
    badge: 'Mercado de Socios',
    heroTitle: 'Tienda de Regalos Say It NOW',
    heroDescription: 'Marcas seleccionadas que hacen que tus palabras tengan más impacto. Cada socio es elegido porque creemos en lo que hacen.',
    exclusiveNote: 'Descuentos exclusivos para miembros Conexión y Legado',
    browseTitle: 'Explorar por Categoría',
    browseSubtitle: 'Selecciona una categoría para ver socios seleccionados',
    clearFilter: 'Limpiar filtro — mostrar todos los socios',
    allPartners: 'Todos los Socios',
    allPartnersDesc: 'Explora todas las marcas del mercado Say It NOW.',
    showingResults: '✓ Mostrando resultados',
    affiliateNote: 'Algunos enlaces son de afiliados. Si decides comprar, Say It NOW puede recibir una pequeña comisión sin costo adicional para ti.',
  },
  fr: {
    badge: 'Marché Partenaire',
    heroTitle: 'Boutique Cadeaux Say It NOW',
    heroDescription: 'Des marques sélectionnées qui donnent plus de poids à vos mots. Chaque partenaire est choisi parce que nous croyons en ce qu\'ils font.',
    exclusiveNote: 'Réductions exclusives pour les membres Connexion et Héritage',
    browseTitle: 'Parcourir par Catégorie',
    browseSubtitle: 'Sélectionnez une catégorie pour voir les partenaires',
    clearFilter: 'Effacer le filtre — afficher tous les partenaires',
    allPartners: 'Tous les Partenaires',
    allPartnersDesc: 'Parcourez toutes les marques du marché Say It NOW.',
    showingResults: '✓ Résultats affichés',
    affiliateNote: 'Certains liens sont des partenariats d\'affiliation. Si vous achetez, Say It NOW peut recevoir une petite commission sans frais supplémentaires pour vous.',
  },
  pt: {
    badge: 'Mercado de Parceiros',
    heroTitle: 'Loja de Presentes Say It NOW',
    heroDescription: 'Marcas selecionadas que fazem suas palavras terem mais impacto. Cada parceiro é escolhido porque acreditamos no que fazem.',
    exclusiveNote: 'Descontos exclusivos para membros Conexão e Legado',
    browseTitle: 'Explorar por Categoria',
    browseSubtitle: 'Selecione uma categoria para ver parceiros selecionados',
    clearFilter: 'Limpar filtro — mostrar todos os parceiros',
    allPartners: 'Todos os Parceiros',
    allPartnersDesc: 'Explore todas as marcas no mercado Say It NOW.',
    showingResults: '✓ Mostrando resultados',
    affiliateNote: 'Alguns links são parcerias de afiliados. Se você comprar, Say It NOW pode receber uma pequena comissão sem custo adicional.',
  },
  ar: {
    badge: 'سوق الشركاء',
    heroTitle: 'متجر هدايا Say It NOW',
    heroDescription: 'علامات تجارية مختارة تجعل كلماتك أكثر تأثيراً. كل شريك مختار لأننا نؤمن بما يفعلونه.',
    exclusiveNote: 'خصومات حصرية لأعضاء الاتصال والإرث',
    browseTitle: 'تصفح حسب الفئة',
    browseSubtitle: 'اختر فئة لرؤية الشركاء المختارين',
    clearFilter: 'مسح الفلتر — عرض جميع الشركاء',
    allPartners: 'جميع الشركاء',
    allPartnersDesc: 'تصفح جميع العلامات التجارية في سوق Say It NOW.',
    showingResults: '✓ عرض النتائج',
    affiliateNote: 'بعض الروابط هي شراكات تابعة. إذا اخترت الشراء، قد يحصل Say It NOW على عمولة صغيرة دون تكلفة إضافية عليك.',
  },
  ja: {
    badge: 'パートナーマーケットプレイス',
    heroTitle: 'Say It NOW ギフトストア',
    heroDescription: 'あなたの言葉をより力強くする厳選ブランド。すべてのパートナーは、私たちが信じるものだから選ばれています。',
    exclusiveNote: 'コネクション＆レガシーメンバー限定割引',
    browseTitle: 'カテゴリで探す',
    browseSubtitle: 'カテゴリを選んで厳選パートナーを見る',
    clearFilter: 'フィルターを解除 — 全パートナーを表示',
    allPartners: 'すべてのパートナー',
    allPartnersDesc: 'Say It NOWマーケットプレイスのすべてのブランドを閲覧。',
    showingResults: '✓ 結果表示中',
    affiliateNote: '一部のリンクはアフィリエイトパートナーシップです。購入を選択された場合、追加費用なしでSay It NOWに少額のコミッションが入る場合があります。',
  },
  ko: {
    badge: '파트너 마켓플레이스',
    heroTitle: 'Say It NOW 기프트 스토어',
    heroDescription: '당신의 말에 더 큰 힘을 주는 엄선된 브랜드. 모든 파트너는 우리가 신뢰하기에 선택되었습니다.',
    exclusiveNote: '커넥션 & 레거시 멤버 전용 할인',
    browseTitle: '카테고리별 탐색',
    browseSubtitle: '카테고리를 선택하여 엄선된 파트너 보기',
    clearFilter: '필터 초기화 — 모든 파트너 보기',
    allPartners: '모든 파트너',
    allPartnersDesc: 'Say It NOW 마켓플레이스의 모든 브랜드를 탐색하세요.',
    showingResults: '✓ 결과 표시 중',
    affiliateNote: '일부 링크는 제휴 파트너십입니다. 구매를 선택하면 추가 비용 없이 Say It NOW에 소액의 수수료가 지급될 수 있습니다.',
  },
  id: {
    badge: 'Pasar Mitra',
    heroTitle: 'Toko Hadiah Say It NOW',
    heroDescription: 'Merek pilihan yang membuat kata-kata Anda lebih berdampak. Setiap mitra dipilih karena kami percaya pada apa yang mereka lakukan.',
    exclusiveNote: 'Diskon eksklusif untuk anggota Koneksi & Warisan',
    browseTitle: 'Jelajahi berdasarkan Kategori',
    browseSubtitle: 'Pilih kategori untuk melihat mitra pilihan',
    clearFilter: 'Hapus filter — tampilkan semua mitra',
    allPartners: 'Semua Mitra',
    allPartnersDesc: 'Jelajahi semua merek di pasar Say It NOW.',
    showingResults: '✓ Menampilkan hasil',
    affiliateNote: 'Beberapa tautan adalah kemitraan afiliasi. Jika Anda memilih untuk membeli, Say It NOW mungkin menerima komisi kecil tanpa biaya tambahan untuk Anda.',
  },

  hi: {
    badge: 'पार्टनर मार्केटप्लेस',
    heroTitle: 'Say It NOW उपहार स्टोर',
    heroDescription: 'क्यूरेटेड ब्रांड जो आपके शब्दों को और भी अधिक प्रभावशाली बनाते हैं। प्रत्येक पार्टनर को हाथ से चुना जाता है क्योंकि हम उनके काम में विश्वास करते हैं।',
    exclusiveNote: 'Connection & Legacy सदस्यों के लिए विशेष छूट',
    browseTitle: 'श्रेणी के अनुसार ब्राउज़ करें',
    browseSubtitle: 'क्यूरेटेड पार्टनर्स देखने के लिए एक श्रेणी चुनें',
    clearFilter: 'फ़िल्टर साफ़ करें — सभी पार्टनर्स दिखाएँ',
    allPartners: 'सभी पार्टनर्स',
    allPartnersDesc: 'Say It NOW मार्केटप्लेस में हर क्यूरेटेड ब्रांड को ब्राउज़ करें।',
    showingResults: '✓ परिणाम दिखाए जा रहे हैं',
    affiliateNote: 'कुछ लिंक सहबद्ध भागीदारी हैं। यदि आप खरीदारी करना चुनते हैं, तो Say It NOW आपको बिना किसी अतिरिक्त लागत के एक छोटा कमीशन कमा सकता है।',
  },
};

export default function GiftStore() {
  const [selectedCategory, setSelectedCategory] = useState('');
  const { data: partners = [], isLoading } = useMarketPartners();
  const { user } = useAuth();
  const { language } = useLanguage();
  const c = lc(copy, language);
  const viewedSlugs = useRef(new Set<string>());
  const partnersRef = useRef<HTMLDivElement>(null);

  const localizedCategories = getLocalizedCategories(language);

  const filteredPartners = selectedCategory
    ? partners.filter((p) => p.category_slug === selectedCategory)
    : partners;

  useEffect(() => {
    filteredPartners.forEach((p) => {
      if (!viewedSlugs.current.has(p.slug)) {
        viewedSlugs.current.add(p.slug);
        trackPartnerCardViewed(p.slug, p.brand_name, p.category_slug);
      }
    });
  }, [filteredPartners]);

  const handleCategorySelect = (slug: string) => {
    setSelectedCategory(slug);
    if (slug && partnersRef.current) {
      setTimeout(() => {
        partnersRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  };

  const activeCategoryData = selectedCategory ? getLocalizedCategory(selectedCategory, language) : null;

  return (
    <div className="min-h-screen flex flex-col bg-background">
      {user ? <AuthenticatedHeader /> : <Header />}

      <main className="flex-1">
        {/* Hero */}
        <section className="bg-hero-gradient pt-16 pb-14 px-4">
          <div className="container max-w-3xl text-center mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-1.5 rounded-full mb-5">
                <Sparkles className="h-3.5 w-3.5" />
                <span className="text-xs font-semibold tracking-wide uppercase">{c.badge}</span>
              </div>

              <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4 leading-tight">
                {c.heroTitle}
              </h1>

              <p className="text-lg text-muted-foreground max-w-xl mx-auto mb-2">
                {c.heroDescription}
              </p>

              <p className="text-sm text-primary font-medium flex items-center justify-center gap-1.5">
                <Heart className="h-3.5 w-3.5 fill-current" />
                {c.exclusiveNote}
              </p>
            </motion.div>
          </div>
        </section>

        {/* Category Grid */}
        <section className="px-4 py-12 container max-w-5xl mx-auto">
          <div className="mb-8 text-center">
            <h2 className="text-xl font-semibold text-foreground mb-1">{c.browseTitle}</h2>
            <p className="text-sm text-muted-foreground">{c.browseSubtitle}</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {localizedCategories.map((cat, i) => (
              <CategoryCard
                key={cat.slug}
                category={cat}
                isSelected={selectedCategory === cat.slug}
                onSelect={handleCategorySelect}
                index={i}
                showingLabel={c.showingResults}
              />
            ))}
          </div>

          {selectedCategory && (
            <div className="flex justify-center mt-6">
              <button
                onClick={() => setSelectedCategory('')}
                className="text-sm text-muted-foreground hover:text-foreground underline underline-offset-4 transition-colors"
              >
                {c.clearFilter}
              </button>
            </div>
          )}
        </section>

        {/* Partners Section */}
        <section
          ref={partnersRef}
          className="px-4 pb-16 container max-w-5xl mx-auto scroll-mt-20"
        >
          <AnimatePresence mode="wait">
            {activeCategoryData ? (
              <motion.div
                key={activeCategoryData.slug}
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.25 }}
                className="mb-8"
              >
                <div className="flex items-center gap-3 mb-1">
                  <activeCategoryData.Icon className="h-5 w-5 text-primary" />
                  <h2 className="text-xl font-bold text-foreground">{activeCategoryData.title}</h2>
                </div>
                <p className="text-sm text-muted-foreground ml-8">{activeCategoryData.description}</p>
              </motion.div>
            ) : (
              <motion.div
                key="all"
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.25 }}
                className="mb-8"
              >
                <h2 className="text-xl font-bold text-foreground mb-1">{c.allPartners}</h2>
                <p className="text-sm text-muted-foreground">{c.allPartnersDesc}</p>
              </motion.div>
            )}
          </AnimatePresence>

          {isLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-48 rounded-2xl" />
              ))}
            </div>
          ) : (
            <AnimatePresence mode="wait">
              <motion.div
                key={selectedCategory || 'all'}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
              >
                {filteredPartners.length > 0 ? (
                  filteredPartners.map((partner, i) => (
                    <PartnerCard key={partner.id} partner={partner} index={i} />
                  ))
                ) : (
                  <PartnerEmptyState />
                )}
              </motion.div>
            </AnimatePresence>
          )}
        </section>

        {/* Affiliate Disclosure */}
        <section className="px-4 pb-10">
          <div className="container max-w-5xl mx-auto">
            <p className="text-xs text-muted-foreground/70 text-center border-t border-border/50 pt-6">
              {c.affiliateNote}
            </p>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
